/**
 * NavBar State Hooks
 *
 * IMPORTANT: When modifying the useIs*Flashing hooks, run WhatsNext tests:
 *   npm test -- --testPathPattern="WhatsNext"
 *
 * The "flashing" hooks are consumed by NavBar UI components but their state
 * originates from Dashboard/WhatsNext actions. The WhatsNext.test.js file
 * contains integration tests that render NavBar + WhatsNext together to verify
 * the arrow-drawing flow works correctly.
 *
 * @see Dashboard/components/WhatsNext/WhatsNext.test.js - "arrow flow integration"
 * @see NavBar/testUtils.js for more context on related tests
 */
import { animate, useMotionValue, useTransform } from "motion/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useNewGivingUI, usePermissions } from "Flocknote/apiHooks";
import { useFlocknoteAppState } from "Flocknote/state";

import {
  useAddAdminActiveAction,
  useAddFundActiveAction,
  useAddGiftsActiveAction,
  useAddMemberActiveAction,
  useCreateCampaignActiveAction,
  useCreateGroupActiveAction,
  useCreateProfileFieldActiveAction,
  useCreateReportActiveAction,
  useCreateSignupActiveAction,
  useCustomizeHomepageActiveAction,
  useEnableContactUsActiveAction,
  useImportMemberActiveAction,
  useNavButtonClicked,
  useProfileImageActiveAction,
  useReviewDuplicatesActiveAction,
  useReviewPrayerRequestsActiveAction,
  useRunDonorSummariesActiveAction,
  useSendWelcomeEmailActiveAction,
  useSetupDatabaseActiveAction,
  useStartRESetupActiveAction,
  useStartRESignupActiveAction,
  useTtjKeywordActiveAction,
} from "Dashboard/components/WhatsNext/whatsNextStore";

import useBackgroundManagerHeight from "Utilities/Hooks/useBackgroundManagerHeight";
import { useWindowSize } from "Utilities/Hooks/useWindowSize";

import {
  navSectionKeys,
  useActiveSectionKey,
  useActiveSectionsNumber,
  useFirstSectionKey,
  useIsExpanded,
  useNavIsScrollable,
} from "./navBarStore";
import {
  closedNavSectionHeightSm,
  fullScreenBreakpoint,
  inactiveNavSectionMarginBottom,
  inactiveNavSectionPaddingTop,
  springAnimation,
} from "./styleConstants";

const useManageCollapseStateOnResize = () => {
  const isFullScreen = useIsFullScreen();
  const [, setIsExpanded] = useIsExpanded();

  useEffect(() => {
    if (isFullScreen) setIsExpanded(false);
    else setIsExpanded(true);
  }, [isFullScreen, setIsExpanded]);
};

/**
 * Hook that determines if the composer is currently minimized.
 * Used to derive the effective NavBar expanded state.
 */
const useIsComposerMinimized = () => {
  const [{ composer }] = useFlocknoteAppState();
  return composer.minimized;
};

/**
 * Hook that computes the effective expanded state of the NavBar.
 * The NavBar should be collapsed when the composer is minimized
 * (e.g., during signup creation flow), regardless of user preference.
 *
 * Returns [effectiveIsExpanded, setIsExpanded] tuple for API compatibility.
 * The viewport-based collapse is already handled by useManageCollapseStateOnResize
 * which sets the raw isExpanded state.
 */
const useEffectiveIsExpanded = () => {
  const [isExpanded, setIsExpanded] = useIsExpanded();
  const isComposerMinimized = useIsComposerMinimized();

  const effectiveIsExpanded = useMemo(
    () => (isComposerMinimized ? false : isExpanded),
    [isExpanded, isComposerMinimized]
  );

  return [effectiveIsExpanded, setIsExpanded];
};

/**
 * Hook that determines if the current window width is at or below the full screen breakpoint.
 *
 * @returns {boolean}
 */
const useIsFullScreen = () => {
  const { width: windowWidth } = useWindowSize({ excludeScrollbar: true });

  return windowWidth <= fullScreenBreakpoint;
};

/** @returns {number} */
const usePrimaryNavWidth = () =>
  useMemo(
    () =>
      parseFloat(
        getComputedStyle(document.documentElement)
          // This value is set in www/css/a_all.css.php. Look for "--nav-width"
          .getPropertyValue("--nav-width")
          .trim()
      ) || 340,
    []
  );

/**
 * Hook that manages and animates the navigation width, and keeps the CSS custom property
 * `--nav-width` in sync with the animated value, which is used to calculate width in legacy pages.
 *
 * @remarks
 * - Reads the initial value of `--nav-width` from the root element's computed styles.
 * - Updates `--nav-width` in real-time as the width animates.
 * - On full-screen layouts, the width is either the full window width (if isExpanded) or `0`.
 * - On non-full-screen layouts, the width is either the initial CSS-defined nav width or a collapsed width (from internal constant).
 * - The custom property is the only output; nothing reads the width from React.
 *   Publishing each frame into the nav store instead re-renders the whole NavBar
 *   per frame, and TopArea and AccountAndSettings are not memoized.
 */
const useExportedNavWidth = () => {
  const primaryNavWidth = usePrimaryNavWidth();
  const isFullScreen = useIsFullScreen();
  const isComposerMinimized = useIsComposerMinimized();

  const goalWidth = useMemo(() => {
    return isFullScreen || isComposerMinimized ? 0 : primaryNavWidth;
  }, [primaryNavWidth, isFullScreen, isComposerMinimized]);

  const navWidth = useMotionValue(goalWidth);
  const width = useTransform(navWidth, (newWidth) => `${newWidth}px`);
  const initRef = useRef();

  useEffect(() => {
    if (!initRef.current)
      document.documentElement.style.setProperty(
        "--nav-width",
        `${goalWidth}px`
      );
    initRef.current = true;
  }, [goalWidth]);

  useEffect(() => {
    const unsubscribe = width.on("change", (latestWidth) => {
      document.documentElement.style.setProperty("--nav-width", latestWidth);
    });

    return unsubscribe;
  }, [width]);

  useEffect(() => {
    const animation = animate(navWidth, goalWidth, springAnimation);
    return () => animation.stop();
  }, [navWidth, goalWidth]);
};

/**
 * Hook that exports the --floating-toolbar-height CSS variable.
 * Set to 68px on mobile (isFullScreen) to account for the FloatingToolbar,
 * 0px on desktop where the toolbar is inside the nav.
 * Following the same pattern as useExportedNavWidth.
 */
const useExportedFloatingToolbarHeight = () => {
  const isFullScreen = useIsFullScreen();

  useEffect(() => {
    const height = isFullScreen ? "68px" : "0px";
    document.documentElement.style.setProperty(
      "--floating-toolbar-height",
      height
    );
  }, [isFullScreen]);
};

const useCanSeeMinistryTools = () => {
  const [
    {
      canSeePrayerPortalNav,
      canSeeReportsMenuNav,
      canSeeMMGSetup,
      canManageHomePage,
    },
  ] = usePermissions();
  return useMemo(
    () =>
      canSeePrayerPortalNav ||
      canSeeReportsMenuNav ||
      canManageHomePage ||
      canSeeMMGSetup,
    [
      canManageHomePage,
      canSeeMMGSetup,
      canSeePrayerPortalNav,
      canSeeReportsMenuNav,
    ]
  );
};

const useCanSeeHouseholdDatabase = () => {
  const [
    {
      canSeeSubnetworksNav,
      canSeeParishInsightsNav,
      canSeeFamilyRegistrationSetup,
      canSeeDatabaseSetup,
      canSeeProfileFieldsSetup,
      canSeeHouseholdDatabaseNav,
      canSeeHealthReportNav,
      canSeeDuplicateFinder,
      canSeeChangeLog,
    },
  ] = usePermissions();

  return useMemo(
    () =>
      canSeeSubnetworksNav ||
      canSeeParishInsightsNav ||
      canSeeFamilyRegistrationSetup ||
      canSeeDatabaseSetup ||
      canSeeProfileFieldsSetup ||
      canSeeHouseholdDatabaseNav ||
      canSeeHealthReportNav ||
      canSeeDuplicateFinder ||
      canSeeChangeLog,
    [
      canSeeChangeLog,
      canSeeDatabaseSetup,
      canSeeDuplicateFinder,
      canSeeFamilyRegistrationSetup,
      canSeeHealthReportNav,
      canSeeHouseholdDatabaseNav,
      canSeeParishInsightsNav,
      canSeeProfileFieldsSetup,
      canSeeSubnetworksNav,
    ]
  );
};

const useCanSeeGiving = () => {
  const [
    { canViewAllGivingData, canAddGifts, canSeeCampaignsNav, canSeeMMGSetup },
  ] = usePermissions();

  return useMemo(
    () =>
      canViewAllGivingData ||
      canAddGifts ||
      canSeeCampaignsNav ||
      canSeeMMGSetup,
    [canAddGifts, canViewAllGivingData, canSeeCampaignsNav, canSeeMMGSetup]
  );
};

const useCanSeeSignupsNav = () => {
  const [{ canSeeSignupsNav }] = usePermissions();

  return useMemo(() => canSeeSignupsNav, [canSeeSignupsNav]);
};

const useCloseNavIfOpen = () => {
  const [isExpanded, setIsExpanded] = useEffectiveIsExpanded();
  const isFullScreen = useIsFullScreen();

  return useCallback(() => {
    if (isFullScreen && isExpanded) setIsExpanded(false);
  }, [isExpanded, isFullScreen, setIsExpanded]);
};

const useGoToActionCenter = () => {
  const [, setActiveSectionKey] = useActiveSectionKey();
  const closeNavIfOpen = useCloseNavIfOpen();

  return useCallback(() => {
    setActiveSectionKey(navSectionKeys.nothing);
    window.fnReactBus.goToUrl("/action-center");
    closeNavIfOpen();
  }, [setActiveSectionKey, closeNavIfOpen]);
};

const useIsSignupsAndEventsSquiggleFlashing = () => {
  const isCreateSignupActive = useCreateSignupActiveAction();
  const isStartRESignupActive = useStartRESignupActiveAction();
  const isStartRESetupActive = useStartRESetupActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isCreateSignupActive || isStartRESignupActive || isStartRESetupActive) &&
      activeSectionKey !== navSectionKeys.signups,
    [
      isCreateSignupActive,
      isStartRESignupActive,
      isStartRESetupActive,
      activeSectionKey,
    ]
  );
};

const useIsCreateSignupButtonFlashing = () => {
  const isCreateSignupActive = useCreateSignupActiveAction();
  const isStartRESignupActive = useStartRESignupActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [isNavButtonClicked] = useNavButtonClicked();

  return useMemo(
    () =>
      (isCreateSignupActive || isStartRESignupActive) &&
      activeSectionKey === navSectionKeys.signups &&
      !isNavButtonClicked,
    [
      isCreateSignupActive,
      isStartRESignupActive,
      activeSectionKey,
      isNavButtonClicked,
    ]
  );
};

const useIsRESetupLinkFlashing = () => {
  const isStartRESetupActive = useStartRESetupActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isStartRESetupActive && activeSectionKey === navSectionKeys.signups,
    [isStartRESetupActive, activeSectionKey]
  );
};

const useIsAddMemberButtonFlashing = () => {
  const isAddMemberActive = useAddMemberActiveAction();

  return isAddMemberActive;
};

const useIsTextToJoinButtonFlashing = () => {
  const isTtjKeywordActive = useTtjKeywordActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isTtjKeywordActive && activeSectionKey === navSectionKeys.groups,
    [isTtjKeywordActive, activeSectionKey]
  );
};

const useIsCreateGroupButtonFlashing = () => {
  const isCreateGroupActive = useCreateGroupActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isCreateGroupActive && activeSectionKey === navSectionKeys.groups,
    [isCreateGroupActive, activeSectionKey]
  );
};

const useIsUserButtonFlashing = () => {
  const isProfileImageActive = useProfileImageActiveAction();

  return isProfileImageActive;
};

const useIsViewProfileFlashing = () => {
  const isProfileImageActive = useProfileImageActiveAction();

  return isProfileImageActive;
};

const useIsGroupCommunicationSquiggleFlashing = () => {
  const isAddAdminActive = useAddAdminActiveAction();
  const isCreateGroupActive = useCreateGroupActiveAction();
  const isTtjKeywordActive = useTtjKeywordActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isAddAdminActive || isCreateGroupActive || isTtjKeywordActive) &&
      activeSectionKey !== navSectionKeys.groups,
    [
      isAddAdminActive,
      isCreateGroupActive,
      isTtjKeywordActive,
      activeSectionKey,
    ]
  );
};

const useIsGroupCommunicationSettingsFlashing = () => {
  const isTtjKeywordActive = useTtjKeywordActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isTtjKeywordActive && activeSectionKey === navSectionKeys.groups,
    [isTtjKeywordActive, activeSectionKey]
  );
};

const useIsEveryoneLinkFlashing = () => {
  const [isNavButtonClicked] = useNavButtonClicked();
  const isAddAdminActive = useAddAdminActiveAction();

  return isAddAdminActive && !isNavButtonClicked;
};

const useIsImportMemberButtonFlashing = () => {
  const isImportMemberActive = useImportMemberActiveAction();

  return useMemo(() => isImportMemberActive, [isImportMemberActive]);
};

const useIsAddGiftsLinkFlashing = () => {
  const isAddGiftsActive = useAddGiftsActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isAddGiftsActive && activeSectionKey === navSectionKeys.giving,
    [isAddGiftsActive, activeSectionKey]
  );
};

const useIsGivingSummaryLinkFlashing = () => {
  const isAddFundActive = useAddFundActiveAction();
  const isCreateCampaignActive = useCreateCampaignActiveAction();
  const isRunDonorSummariesActive = useRunDonorSummariesActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [navButtonClicked] = useNavButtonClicked();
  const { hideLegacyGivingUI } = useNewGivingUI();

  // AddFund/CreateCampaign/RunDonorSummaries target dedicated links in the new
  // giving UI, so only flash Summary for these actions when the legacy UI is
  // active.
  const addFundFlashesSummary = isAddFundActive && !hideLegacyGivingUI;
  const createCampaignFlashesSummary =
    isCreateCampaignActive && !hideLegacyGivingUI;
  const runDonorSummariesFlashesSummary =
    isRunDonorSummariesActive && !hideLegacyGivingUI;

  return useMemo(
    () =>
      (addFundFlashesSummary ||
        createCampaignFlashesSummary ||
        runDonorSummariesFlashesSummary) &&
      activeSectionKey === navSectionKeys.giving &&
      !navButtonClicked,
    [
      addFundFlashesSummary,
      createCampaignFlashesSummary,
      runDonorSummariesFlashesSummary,
      activeSectionKey,
      navButtonClicked,
    ]
  );
};

const useIsGivingFundsLinkFlashing = () => {
  const isAddFundActive = useAddFundActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [navButtonClicked] = useNavButtonClicked();
  const { hideLegacyGivingUI } = useNewGivingUI();

  return useMemo(
    () =>
      isAddFundActive &&
      hideLegacyGivingUI &&
      activeSectionKey === navSectionKeys.giving &&
      !navButtonClicked,
    [isAddFundActive, hideLegacyGivingUI, activeSectionKey, navButtonClicked]
  );
};

const useIsGivingBatchesLinkFlashing = () => {
  const isAddGiftsActive = useAddGiftsActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [navButtonClicked] = useNavButtonClicked();
  const { hideLegacyGivingUI } = useNewGivingUI();

  return useMemo(
    () =>
      isAddGiftsActive &&
      hideLegacyGivingUI &&
      activeSectionKey === navSectionKeys.giving &&
      !navButtonClicked,
    [isAddGiftsActive, hideLegacyGivingUI, activeSectionKey, navButtonClicked]
  );
};

const useIsGivingDonorsLinkFlashing = () => {
  const isRunDonorSummariesActive = useRunDonorSummariesActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [navButtonClicked] = useNavButtonClicked();
  const { hideLegacyGivingUI } = useNewGivingUI();

  return useMemo(
    () =>
      isRunDonorSummariesActive &&
      hideLegacyGivingUI &&
      activeSectionKey === navSectionKeys.giving &&
      !navButtonClicked,
    [
      isRunDonorSummariesActive,
      hideLegacyGivingUI,
      activeSectionKey,
      navButtonClicked,
    ]
  );
};

const useIsGivingCampaignsLinkFlashing = () => {
  const isCreateCampaignActive = useCreateCampaignActiveAction();
  const [activeSectionKey] = useActiveSectionKey();
  const [navButtonClicked] = useNavButtonClicked();
  const { hideLegacyGivingUI } = useNewGivingUI();

  return useMemo(
    () =>
      isCreateCampaignActive &&
      hideLegacyGivingUI &&
      activeSectionKey === navSectionKeys.giving &&
      !navButtonClicked,
    [
      isCreateCampaignActive,
      hideLegacyGivingUI,
      activeSectionKey,
      navButtonClicked,
    ]
  );
};

const useIsGivingSquiggleFlashing = () => {
  const isAddGiftsActive = useAddGiftsActiveAction();
  const isAddFundActive = useAddFundActiveAction();
  const isCreateCampaignActive = useCreateCampaignActiveAction();
  const isRunDonorSummariesActive = useRunDonorSummariesActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isAddGiftsActive ||
        isAddFundActive ||
        isCreateCampaignActive ||
        isRunDonorSummariesActive) &&
      activeSectionKey !== navSectionKeys.giving,
    [
      isAddGiftsActive,
      isAddFundActive,
      isCreateCampaignActive,
      isRunDonorSummariesActive,
      activeSectionKey,
    ]
  );
};

const useIsReportBuilderLinkFlashing = () => {
  const isCreateReportActive = useCreateReportActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isCreateReportActive && activeSectionKey === navSectionKeys.tools,
    [isCreateReportActive, activeSectionKey]
  );
};

const useIsPrayerRequestsLinkFlashing = () => {
  const isReviewPrayerRequestsActive = useReviewPrayerRequestsActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      isReviewPrayerRequestsActive && activeSectionKey === navSectionKeys.tools,
    [isReviewPrayerRequestsActive, activeSectionKey]
  );
};

const useIsHomePageLinkFlashing = () => {
  const isCustomizeHomepageActive = useCustomizeHomepageActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      isCustomizeHomepageActive && activeSectionKey === navSectionKeys.tools,
    [isCustomizeHomepageActive, activeSectionKey]
  );
};

const useIsMinistryToolsSquiggleFlashing = () => {
  const isCreateReportActive = useCreateReportActiveAction();
  const isReviewPrayerRequestsActive = useReviewPrayerRequestsActiveAction();
  const isCustomizeHomepageActive = useCustomizeHomepageActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isCreateReportActive ||
        isReviewPrayerRequestsActive ||
        isCustomizeHomepageActive) &&
      activeSectionKey !== navSectionKeys.tools,
    [
      isCreateReportActive,
      isReviewPrayerRequestsActive,
      isCustomizeHomepageActive,
      activeSectionKey,
    ]
  );
};

const useIsHouseholdDatabaseSquiggleFlashing = () => {
  const isReviewDuplicatesActive = useReviewDuplicatesActiveAction();
  const isCreateProfileFieldActive = useCreateProfileFieldActiveAction();
  const isSetupDatabaseActive = useSetupDatabaseActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isReviewDuplicatesActive ||
        isCreateProfileFieldActive ||
        isSetupDatabaseActive) &&
      activeSectionKey !== navSectionKeys.database,
    [
      isReviewDuplicatesActive,
      isCreateProfileFieldActive,
      isSetupDatabaseActive,
      activeSectionKey,
    ]
  );
};

const useIsHouseholdDatabaseSettingsFlashing = () => {
  const isCreateProfileFieldActive = useCreateProfileFieldActiveAction();
  const isSetupDatabaseActive = useSetupDatabaseActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      (isCreateProfileFieldActive || isSetupDatabaseActive) &&
      activeSectionKey === navSectionKeys.database,
    [isCreateProfileFieldActive, isSetupDatabaseActive, activeSectionKey]
  );
};

const useIsDuplicateFinderButtonFlashing = () => {
  const isReviewDuplicatesActive = useReviewDuplicatesActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      isReviewDuplicatesActive && activeSectionKey === navSectionKeys.database,
    [isReviewDuplicatesActive, activeSectionKey]
  );
};

const useIsDuplicateFinderLinkFlashing = () => {
  const isReviewDuplicatesActive = useReviewDuplicatesActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      isReviewDuplicatesActive && activeSectionKey === navSectionKeys.database,
    [isReviewDuplicatesActive, activeSectionKey]
  );
};

const useIsProfileFieldsButtonFlashing = () => {
  const isCreateProfileFieldActive = useCreateProfileFieldActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () =>
      isCreateProfileFieldActive &&
      activeSectionKey === navSectionKeys.database,
    [isCreateProfileFieldActive, activeSectionKey]
  );
};

const useIsDatabaseSetupButtonFlashing = () => {
  const isSetupDatabaseActive = useSetupDatabaseActiveAction();
  const [activeSectionKey] = useActiveSectionKey();

  return useMemo(
    () => isSetupDatabaseActive && activeSectionKey === navSectionKeys.database,
    [isSetupDatabaseActive, activeSectionKey]
  );
};

const useIsSettingsButtonFlashing = () => {
  const isEnableContactUsActive = useEnableContactUsActiveAction();
  const isSendWelcomeEmailActive = useSendWelcomeEmailActiveAction();
  const { pathname } = useLocation();
  const isOnContactSettingsPage = pathname.includes(
    "/settings/network-welcome"
  );

  return (
    (isEnableContactUsActive || isSendWelcomeEmailActive) &&
    !isOnContactSettingsPage
  );
};

const useIsContactSettingsButtonFlashing = () => {
  const isEnableContactUsActive = useEnableContactUsActiveAction();
  const isSendWelcomeEmailActive = useSendWelcomeEmailActiveAction();
  return isEnableContactUsActive || isSendWelcomeEmailActive;
};

/**
 * Returns the number of pixels to shrink the NavBar container when all three
 * conditions are met:
 * 1. Desktop view (!isFullScreen)
 * 2. Nav content is scrollable
 * 3. The legacy Background Tasks panel is popped out
 *
 * The +8 extends the NavBar slightly past the panel's top edge, covering the
 * sliver gap caused by --app-border-width (4px). The panel's higher z-index
 * (7002 vs 3000) ensures it still renders on top.
 */
const useNavContainerShrink = () => {
  const navIsScrollable = useNavIsScrollable();
  const isFullScreen = useIsFullScreen();
  const backgroundManagerHeight = useBackgroundManagerHeight();

  return useMemo(
    () =>
      !isFullScreen && navIsScrollable && backgroundManagerHeight > 0
        ? backgroundManagerHeight + 8
        : 0,
    [isFullScreen, navIsScrollable, backgroundManagerHeight]
  );
};

/**
 * Returns the background-manager height that internal nav layout should
 * account for. When the NavBar container is already shrunk to avoid the panel,
 * internal components don't need to compensate — so this returns 0 (or -8 to
 * cover the border sliver). When the container is not shrunk, returns the raw
 * panel height so layout can compensate manually.
 */
const useEffectiveBgManagerHeight = () => {
  const backgroundManagerHeight = useBackgroundManagerHeight();
  const containerShrink = useNavContainerShrink();

  return useMemo(
    () => backgroundManagerHeight - containerShrink,
    [backgroundManagerHeight, containerShrink]
  );
};

/**
 * Returns an animated MotionValue (in px) that tracks the background-manager
 * height offset, springing in sync with the nav-width animation.
 *
 * @returns {import("framer-motion").MotionValue<string>} e.g. "42px"
 */
const useAnimatedBgManagerOffset = () => {
  const goalOffset = useNavContainerShrink();
  const offsetMv = useMotionValue(goalOffset);
  const offsetPx = useTransform(offsetMv, (v) => `${v}px`);

  useEffect(() => {
    const animation = animate(offsetMv, goalOffset, springAnimation);
    return () => animation.stop();
  }, [offsetMv, goalOffset]);

  return offsetPx;
};

/**
 * Returns the total layout space occupied by all inactive NavSections.
 *
 * The per-section cost depends on whether the section has `isFirst`
 * (no padding-top) or not (padding-top adds to the height since
 * box-sizing is content-box). Every inactive section also has a
 * negative bottom margin.
 *
 * Layout cost per inactive section:
 *   - isFirst:  closedNavSectionHeightSm + 0 + inactiveNavSectionMarginBottom
 *   - other:    closedNavSectionHeightSm + inactiveNavSectionPaddingTop + inactiveNavSectionMarginBottom
 */
const useInactiveSectionSpace = () => {
  const activeSectionsNumber = useActiveSectionsNumber();
  const [activeSectionKey] = useActiveSectionKey();
  const firstSectionKey = useFirstSectionKey();

  return useMemo(() => {
    const inactiveCount = activeSectionsNumber - 1;
    if (inactiveCount <= 0) return 0;

    const basePerSection =
      closedNavSectionHeightSm + inactiveNavSectionMarginBottom;
    const firstIsInactive = firstSectionKey !== activeSectionKey;
    const sectionsWithPadding = firstIsInactive
      ? inactiveCount - 1
      : inactiveCount;

    return (
      inactiveCount * basePerSection +
      sectionsWithPadding * inactiveNavSectionPaddingTop
    );
  }, [activeSectionKey, activeSectionsNumber, firstSectionKey]);
};

export {
  useManageCollapseStateOnResize,
  useEffectiveIsExpanded,
  useIsComposerMinimized,
  useIsFullScreen,
  useExportedFloatingToolbarHeight,
  useExportedNavWidth,
  useCanSeeMinistryTools,
  useCanSeeHouseholdDatabase,
  useCanSeeGiving,
  usePrimaryNavWidth,
  useCloseNavIfOpen,
  useGoToActionCenter,
  useCanSeeSignupsNav,
  useIsSignupsAndEventsSquiggleFlashing,
  useIsCreateSignupButtonFlashing,
  useIsRESetupLinkFlashing,
  useIsAddMemberButtonFlashing,
  useIsTextToJoinButtonFlashing,
  useIsCreateGroupButtonFlashing,
  useIsUserButtonFlashing,
  useIsViewProfileFlashing,
  useIsGroupCommunicationSquiggleFlashing,
  useIsGroupCommunicationSettingsFlashing,
  useIsEveryoneLinkFlashing,
  useIsImportMemberButtonFlashing,
  useIsAddGiftsLinkFlashing,
  useIsGivingSummaryLinkFlashing,
  useIsGivingFundsLinkFlashing,
  useIsGivingBatchesLinkFlashing,
  useIsGivingDonorsLinkFlashing,
  useIsGivingCampaignsLinkFlashing,
  useIsGivingSquiggleFlashing,
  useIsReportBuilderLinkFlashing,
  useIsPrayerRequestsLinkFlashing,
  useIsHomePageLinkFlashing,
  useIsMinistryToolsSquiggleFlashing,
  useIsHouseholdDatabaseSquiggleFlashing,
  useIsHouseholdDatabaseSettingsFlashing,
  useIsDuplicateFinderButtonFlashing,
  useIsDuplicateFinderLinkFlashing,
  useIsProfileFieldsButtonFlashing,
  useIsDatabaseSetupButtonFlashing,
  useIsSettingsButtonFlashing,
  useIsContactSettingsButtonFlashing,
  useEffectiveBgManagerHeight,
  useAnimatedBgManagerOffset,
  useInactiveSectionSpace,
};
